import * as api from "@/api";
import {
	clearModels,
	createModel,
	createModels,
	deleteModels,
	editorInstance,
	getValue,
	readMode,
	setValue,
} from "@/code-editor";
import { importPizZip } from "@/dependencies";
import globals from "@/globals";
import { showSettings } from "@/settings";
import * as signals from "@/signals";
import { deleteBackup, getDomain, getSuffix, isSiteOpened } from "@/site";
import {
	BinaryFile,
	Column,
	FileUploadInfo,
	FileUploadStatus,
	PresetSaveCallback,
	SiteConfig,
	SiteContent,
	UploadResponse,
	UploadSiteRequest,
} from "@/types";
import {
	closeAiBar,
	closeDialog,
	getPremium,
	getRthCdnBaseUrl,
	showDialog,
	showFloatNotification,
	showPrompt,
	showToast,
	sleep,
} from "@/utils";
import { t } from "i18next";
import PizZip from "pizzip";
import { MouseEvent } from "react";

const FOREIGN_CDN_DOMAINS = Object.freeze([
	"cdnjs.cloudflare.com",
	"cdn.jsdelivr.net",
	"cdn.tailwindcss.com",
	"code.jquery.com",
	"googleapis.com",
	"unpkg.com",
]);
const FOREIGN_IMAGE_HOST_DOMAINS = Object.freeze(["loli.net"]);

const fileInput = ((): HTMLInputElement => {
	const newInput = document.createElement("input");
	newInput.type = "file";
	return newInput;
})();

function checkForeignAssets(): boolean {
	const value = getValue();
	for (const domain of FOREIGN_CDN_DOMAINS) {
		if (value.includes(domain)) {
			showFloatNotification({
				text: t("usingForeignPublicCdn", { domain }),
				title: t("warning"),
			});
			return true;
		}
	}
	for (const domain of FOREIGN_IMAGE_HOST_DOMAINS) {
		if (value.includes(domain)) {
			showFloatNotification({
				text: t("usingForeignImageHosting", { domain }),
				title: t("warning"),
			});
			return true;
		}
	}
	return false;
}

export function configExists(content?: SiteContent): content is SiteContent {
	if (!content || !content["settings.rth"]) {
		void showDialog(t("configNotFound"));
		return false;
	}
	return true;
}

export async function deleteFile(name: string): Promise<void> {
	if (signals.selectedFiles.value.length > 0) {
		await showDialog(t("confirmDeleteMultipleFiles"), {
			showCancel: true,
		});
		const siteContent = signals.files.value;
		if (!configExists(siteContent)) {
			return;
		}
		const config = siteContent["settings.rth"];
		const filesToBeDeleted = new Set([
			...signals.selectedFiles.value,
			signals.currentFile.value,
		]);
		deleteFilesInSiteContent(filesToBeDeleted);
		deleteModels(filesToBeDeleted);
		void save({
			keys: [...filesToBeDeleted],
		});
		signals.selectedFiles.value = [];
		void openFile(config.home);
	} else {
		await showDialog(
			t("confirmDelete", {
				name: name,
			}),
			{
				showCancel: true,
			},
		);
		const siteContent = signals.files.value;
		if (!configExists(siteContent)) {
			return;
		}
		const config = siteContent["settings.rth"];
		const filesToBeDeleted = new Set([name]);
		const isFolder = name.endsWith("/");
		if (isFolder) {
			for (const key in siteContent) {
				if (
					key !== name &&
					key !== config.home &&
					key.startsWith(name)
				) {
					filesToBeDeleted.add(key);
				}
			}
		} else if (name.toLowerCase().endsWith(".md")) {
			filesToBeDeleted.add(name + ".html");
		}
		deleteFilesInSiteContent(filesToBeDeleted);
		deleteModels(filesToBeDeleted);
		void save({
			keys: [...filesToBeDeleted],
		});
		void openFile(config.home);
	}
}

function deleteFilesInSiteContent(filesToBeDeleted: Set<string>): void {
	const newFiles = { ...signals.files.value };
	for (const key in newFiles) {
		if (filesToBeDeleted.has(key)) {
			delete newFiles[key];
		}
	}
	signals.files.value = newFiles;
}

export function fileExists(
	filename: string,
	options: {
		isNegative?: boolean;
		noDialog?: boolean;
	} = {},
): boolean {
	if (!globals.accept.test(filename) && !globals.privilege) {
		if (!options.noDialog) {
			showDialog(
				t("premiumRequiredForCreatingThisFileType") +
					t("refreshIfJustPaid"),
				{
					showCancel: true,
				},
			)
				.then(getPremium)
				.catch(console.error);
		}
		return true;
	} else if (signals.files.value[filename] !== undefined) {
		if (!options.isNegative && !options.noDialog) {
			void closeDialog();
			void showDialog(
				t("error") +
					t("alreadyExists", {
						filename: filename,
					}),
			);
		}
		return true;
	} else {
		if (options.isNegative && !options.noDialog) {
			void showDialog(
				t("error") +
					t("doesNotExist", {
						filename: filename,
					}),
			);
		}
		return false;
	}
}

export function fillMissingFolderNames(filenames: string[]): string[] {
	const completedFilenames = new Set<string>(filenames);
	for (const key of filenames) {
		const parts = key.split("/");
		for (let i = 1; i < parts.length; i++) {
			const folderPath = parts.slice(0, i).join("/") + "/";
			completedFilenames.add(folderPath);
		}
	}
	return [...completedFilenames];
}

export function getFolderName(filename: string): string {
	if (!filename.includes("/")) {
		return "";
	}
	const split = filename.split("/");
	split.pop();
	return split.join("/") + "/";
}

export function getUrl(
	isAlwaysHttps: boolean,
	filename = signals.currentFile.value,
	fullDomain = signals.currentSite.value,
): string {
	const siteContent = signals.files.value;
	const config = siteContent["settings.rth"];
	if (filename && configExists(siteContent) && filename === config.home) {
		filename = "";
	}
	const domain = getDomain(fullDomain);
	const suffix = getSuffix(domain);
	return (
		(isAlwaysHttps || !domain.includes(".") ? "https:" : "http:") +
		"//" +
		domain +
		suffix +
		"/" +
		filename
	);
}

export function getValidFilename(value: string, extension = ".html"): string {
	value = value
		.replaceAll("\\", "/")
		.replace(/^\/+/, "") // remove leading slashes
		.replace(/\/+/g, "/") // replace multiple slashes with one
		.replace(/\.+/g, ".")
		.replace(/[^\d\w\u4e00-\u9fa5\-/.]/g, "");
	if (extension === "/" || (!value.includes(".") && !value.endsWith("/"))) {
		value += extension;
	}
	if (value === ".html") {
		value = "untitled" + value;
	} else if (value.endsWith(".md.html")) {
		value = value.substring(0, value.length - 5);
	}
	const maxLen = 255;
	if (value.length > maxLen) {
		value = value.substring(value.length - maxLen);
	}
	return value;
}

export function handleSaveClick(): void {
	if (isFileOpened()) {
		const executable = /\.(html?|md|php)$/i;
		void save({
			callback: executable.test(signals.currentFile.value)
				? PresetSaveCallback.SHOW_DIALOG
				: PresetSaveCallback.SHOW_TOAST,
			key: signals.currentFile.value,
		});
	}
}

function isBinaryFile(
	file?: string | BinaryFile | SiteConfig,
): file is BinaryFile {
	if (typeof file !== "object") {
		return false;
	}
	file = file as BinaryFile;
	return !!file.hash || !!file.md5 || !!file.type || !!file.url;
}

export function isFileOpened(): boolean {
	if (signals.currentFile.value) {
		return true;
	} else {
		void showDialog(t("notOpenedFile"));
		return false;
	}
}

export function isImage(filename: string): boolean {
	const file = signals.files.value[filename];
	return isBinaryFile(file) && !!file.type?.startsWith("image/");
}

export function isRenamable(name: string): boolean {
	return (
		signals.selectedFiles.value.length === 0 &&
		(!!globals.privilege ||
			name !== signals.files.value["settings.rth"]?.home)
	);
}

function moveFile(oldName: string, newName: string): void {
	signals.files.value = {
		...signals.files.value,
		[newName]: signals.files.value[oldName],
	};
	deleteFilesInSiteContent(new Set([oldName]));
}

export async function openFile(
	name: string,
	event?: MouseEvent,
): Promise<void> {
	let hasFileOpened = false;
	signals.currentFile.value = name;
	await closeDialog();
	for (const key in signals.files.value) {
		if (key !== name) {
			continue;
		}
		const currentFile = signals.files.value[signals.currentFile.value];
		if (currentFile === undefined) {
			break;
		}
		hasFileOpened = true;
		if (event && (!name.endsWith("/") || currentFile)) {
			signals.currentColumn.value = Column.CODE_EDITOR;
		}
		document.getElementsByTagName("img")[0]?.remove();
		const mainArea = document.getElementById("main") as HTMLElement;
		if (typeof currentFile === "string") {
			mainArea.classList.remove("img");
			createModels(signals.files.value);
			setValue(currentFile, {
				filename: name,
			});
			readMode();
			checkForeignAssets();
		} else {
			void closeAiBar();
			mainArea.classList.add("img");
			if (isImage(signals.currentFile.value)) {
				const currentImage = currentFile as BinaryFile;
				const newImg = new Image();
				if (currentImage.url?.startsWith("anonymous/")) {
					newImg.src = getUrl(true);
				} else {
					newImg.src =
						getRthCdnBaseUrl() +
						((): string => {
							const fingerprint =
								currentImage.hash || currentImage.md5;
							if (fingerprint) {
								return "cached-" + fingerprint;
							} else if (
								currentImage.url &&
								!currentImage.url.includes(":")
							) {
								return (
									"cached-" + currentImage.url.split("/")[2]
								);
							} else {
								return "0";
							}
						})() +
						"/" +
						getDomain(signals.currentSite.value) +
						"/" +
						signals.currentFile.value;
				}
				newImg.alt = signals.currentFile.value;
				document
					.getElementsByClassName("img-frame")[0]
					.appendChild(newImg);
			} else {
				await showDialog(t("confirmDownloadThisFile"), {
					showCancel: true,
				});
				visitPage();
			}
		}
	}
	if (!hasFileOpened) {
		signals.currentFile.value = "";
		setValue("");
	}
}

export async function prepareUpload(
	files: File[],
	paths?: string[],
): Promise<void> {
	if (files.length === 1 && files[0].name.endsWith(".zip")) {
		readZipFile(files[0]); // will call prepareUpload again
		return;
	}
	const pendingUploadFilesInfo: FileUploadInfo[] = [];
	globals.pendingUploadFiles = {};
	for (let i = 0; i < files.length; i++) {
		const file = files[i];
		const path = paths ? paths[i] : file.name;
		globals.pendingUploadFiles[path] = file;
		pendingUploadFilesInfo.push({
			path: path,
			skipReason: "",
			status: FileUploadStatus.NOT_UPLOADED,
			type: file.type,
		});
	}
	signals.pendingUploadFiles.value = [];
	await sleep(1);
	signals.pendingUploadFiles.value = pendingUploadFilesInfo;
}

export function readTextFile(file: Blob): Promise<string> {
	return new Promise<string>((resolve) => {
		const reader = new FileReader();
		reader.onload = (): void => {
			if (typeof reader.result === "string") {
				resolve(reader.result);
			}
		};
		reader.readAsText(file);
	});
}

export function readZipFile(file: File): void {
	const deleteCommonPrefix = (array: string[]): void => {
		if (array.length === 0) {
			return;
		}
		const prefix = array[0].split("/")[0];
		for (let i = 1; i < array.length; i++) {
			if (array[i].split("/")[0] !== prefix) {
				return;
			}
		}
		for (let i = 0; i < array.length; i++) {
			array[i] = array[i].substring(prefix.length + 1);
		}
	};

	const reader = new FileReader();
	reader.onload = async (): Promise<void> => {
		try {
			const PizZip = await importPizZip();
			const zip = new PizZip(reader.result as PizZip.LoadData);
			const files: File[] = [];
			const names: string[] = [];
			for (const filename in zip.files) {
				if (
					filename.startsWith("__MACOSX/") ||
					filename.includes("node_modules")
				) {
					continue;
				}
				const file = zip.files[filename];
				const filenameLower = filename.toLowerCase();
				if (
					!file.dir &&
					(filenameLower.endsWith(".php") ||
						(globals.accept.test(filenameLower) &&
							!filenameLower.endsWith(".rth")))
				) {
					const fileContent = file.asText();
					if (fileContent) {
						files.push(
							new File(
								[fileContent],
								filename.split("/").pop() || filename,
							),
						);
						names.push(filename);
					}
				}
			}
			deleteCommonPrefix(names);
			void prepareUpload(files, names);
		} catch (error) {
			console.error(error);
			void showDialog(t("unableReadThisZipFile"));
		}
	};
	reader.readAsArrayBuffer(file);
}

function renameFile(oldName: string, newName: string): boolean {
	if (oldName === newName) {
		return false;
	}
	const siteContent = signals.files.value;
	if (!configExists(siteContent)) {
		return false;
	}
	const config = siteContent["settings.rth"];
	const isFolder = oldName.endsWith("/");
	newName = isFolder
		? getValidFilename(newName, "/")
		: getValidFilename(newName);
	if (fileExists(newName)) {
		return false;
	}
	moveFile(oldName, newName);
	if (isFolder) {
		for (const key in signals.files.value) {
			if (key !== oldName && key !== newName && key.startsWith(oldName)) {
				const newChildName = newName + key.substring(oldName.length);
				if (!fileExists(newChildName)) {
					moveFile(key, newChildName);
				}
			}
		}
		clearModels(true);
		createModels(signals.files.value);
	} else {
		const file = signals.files.value[newName];
		if (typeof file === "string") {
			deleteModels(new Set([oldName]));
			createModel(newName, file);
		}
	}
	if (oldName === config.home) {
		config.home = newName;
	}
	if (oldName.endsWith(".js") && signals.files.value[oldName + ".rth"]) {
		moveFile(oldName + ".rth", newName + ".rth");
	} else if (
		oldName.endsWith(".md") &&
		signals.files.value[oldName + ".html"]
	) {
		moveFile(oldName + ".html", newName + ".html");
	}
	return true;
}

export async function renameFilePrompt(name: string): Promise<void> {
	const isFolder = name.endsWith("/");
	const defaultText = isFolder ? name.substring(0, name.length - 1) : name;
	const newName = await showPrompt(t("enterNewFilename"), {
		defaultText: defaultText,
	});
	if (!newName) {
		return;
	}
	renameFiles(new Map([[name, newName]]));
}

export function renameFiles(nameMap: Map<string, string>): void {
	for (const [oldName, newName] of nameMap) {
		if (!renameFile(oldName, newName)) {
			return;
		}
	}
	void save({
		callback: () => {
			const firstNewName = nameMap.values().next().value as string;
			void openFile(firstNewName);
		},
		content: Object.fromEntries(nameMap),
		type: "rename",
	});
}

export function resetFileList(): void {
	signals.changedFiles.value = [];
	signals.currentFile.value = "";
	signals.currentFolder.value = "";
	signals.files.value = {} as SiteContent;
	signals.hasUnsaved.value = false;
	signals.pendingUploadFiles.value = [];
	signals.selectedFiles.value = [];
	globals.pendingUploadFiles = {};
}

export async function save({
	callback,
	content,
	hideLoading,
	key,
	keys,
	type,
}: {
	callback?: (() => void) | PresetSaveCallback;
	content?: string | File | BinaryFile | SiteConfig | SiteContent;
	hideLoading?: boolean;
	key?: string;
	keys?: string[];
	type?: "object" | "rename" | "string" | "undefined";
} = {}): Promise<boolean> {
	if (!isSiteOpened()) {
		return false;
	}
	if (key) {
		signals.changedFiles.value = signals.changedFiles.value.filter(
			(item) => item !== key,
		);
	} else {
		signals.changedFiles.value = [];
	}
	signals.hasUnsaved.value = false;
	globals.currentHostInitial = JSON.parse(
		JSON.stringify(signals.files.value),
	) as SiteContent;
	const domain = getDomain(signals.currentSite.value);
	const postData: UploadSiteRequest = {
		content: undefined,
		key: "",
		keys: "",
		privilege: globals.privilege || undefined,
		processor: undefined,
		type: "",
	};
	let keepFloat = false;
	if (type === "rename") {
		content = content as SiteContent;
		postData.content = JSON.stringify(content);
		postData.type = "rename";
	} else if (key) {
		// save a single file
		if (key.includes("/")) {
			// create non-existing folders
			const folderName = getFolderName(key);
			if (!signals.files.value[folderName]) {
				signals.files.value = {
					...signals.files.value,
					[folderName]: "",
				};
			}
		}
		postData.key = key;
		if (typeof content === "undefined") {
			// read file content from state
			content = signals.files.value[key];
		}
		postData.type = type || typeof content;
		if (typeof content === "object" && !(content instanceof File)) {
			content = JSON.stringify(content);
		}
		postData.content = content; // not only string
		if (typeof content === "string") {
			if (key.endsWith(".js")) {
				if (content.includes("jsjiami.com")) {
					keepFloat = true;
					showFloatNotification({
						onClick: showSettings,
						text: t("hasJsEncryptorBuiltIn"),
						title: t("manualJsEncryptionNotNeeded"),
					});
				} else if (globals.settings.jsProcessor) {
					postData.processor = globals.settings.jsProcessor;
				}
			} else if (/\.(html?|php)$/i.test(key)) {
				const atAttr = /\s@[a-z]+=/.exec(content);
				if (atAttr) {
					const attr = atAttr[0].slice(2, -1);
					let tip = t("isNonStandardHtmlAttribute", {
						attr: "@" + attr,
					});
					let correctAttr;
					if (content.includes("vue")) {
						correctAttr = "v-on:" + attr;
					} else if (content.includes("alpinejs")) {
						correctAttr = "x-on:" + attr;
					}
					if (correctAttr) {
						tip +=
							t("space") +
							t("useInstead", {
								sth: correctAttr,
							});
					}
					void showDialog(tip, {
						title: t("failedToSave").toString(),
					});
					return false;
				}
			}
		}
	} else if (keys) {
		// delete files
		postData.keys = JSON.stringify(keys);
		postData.type = "undefined";
	} else {
		// save all
		postData.content = JSON.stringify(signals.files.value);
	}
	if (!hideLoading) {
		signals.isLoadingShown.value = true;
	}
	try {
		const data = await api.uploadSite(domain, postData);
		signals.isLoadingShown.value = false;
		if (uploadCallback(data, keepFloat)) {
			// saved successfully
			deleteBackup();
			if (callback) {
				if (typeof callback === "function") {
					callback();
				} else if (callback === PresetSaveCallback.SHOW_DIALOG) {
					const hasForeignAssets = checkForeignAssets();
					if (hasForeignAssets) {
						await sleep(globals.ANIMATION_WAIT_TIME);
					}
					void showDialog(
						t("confirmOpenWebPage"),
						{
							showCancel: true,
						},
						{
							cancel: () => {
								editorInstance?.focus();
							},
							ok: () => {
								globals.openedPage?.close();
								globals.openedPage = visitPage();
							},
						},
					);
				} else if (callback === PresetSaveCallback.SHOW_TOAST) {
					void showToast(t("saved"));
				}
			}
		}
	} catch (error) {
		void api.handleApiError(error);
		signals.isLoadingShown.value = false;
	}
	return true;
}

export function selectLocalFile(
	{ accept, multiple }: { accept: string; multiple: boolean },
	callback: ((files: FileList) => void | Promise<void>) | null = null,
): void {
	fileInput.accept = accept;
	fileInput.multiple = multiple;
	fileInput.onchange = (): void => {
		if (fileInput.files) {
			if (callback) {
				void callback(fileInput.files);
			} else {
				void prepareUpload([...fileInput.files]);
			}
		}
	};
	fileInput.value = "";
	fileInput.click();
}

export function uploadCallback(
	data: UploadResponse,
	keepFloat = false,
): boolean {
	if (!data) {
		return false;
	}
	if (data.errorLog) {
		console.error(data.errorLog);
		showFloatNotification({
			isFolded: true,
			text: data.errorLog,
			title: t("jsProcessorError"),
		});
	} else if (!keepFloat) {
		signals.floatNotificationInfo.value = null;
	}
	if (data.modified) {
		for (const key in data.modified) {
			signals.files.value = {
				...signals.files.value,
				[key]: data.modified[key],
			};
		}
	}
	if (data.alert) {
		void showDialog(t(data.alert), {
			title: t("failedToSave").toString(),
		});
	}
	return Boolean(data.success);
}

export function visitPage(
	filename = signals.currentFile.value,
	fullDomain = signals.currentSite.value,
): Window | null {
	try {
		return window.open(getUrl(false, filename, fullDomain));
	} catch (error: unknown) {
		console.error(error);
		if (error instanceof Error) {
			void showDialog(error.message);
		}
	}
	return null;
}
